import type { Href } from "expo-router";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useState } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView, Text, View } from "react-native";

import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;

import { StudentPortalShell } from "../../../components/student/StudentPortalShell";
import { useStudentPortalShellData } from "../../../lib/useStudentPortalShellData";
import {
  formatUsd,
  listMyDisbursements,
  placeLabel,
  type StudentDisbursementRow,
} from "../../../lib/winners";


export default function StudentWinningsScreen() {
  const shell = useStudentPortalShellData();
  const [rows, setRows] = useState<StudentDisbursementRow[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    setRows(await listMyDisbursements());
    setLoading(false);
  }, []);

  useFocusEffect(
    useCallback(() => {
      void reload();
    }, [reload])
  );

  return (
    <StudentPortalShell
      topbarTitle="Winnings"
      topbarFirstName={shell.topbarFirstName}
      avatarName={shell.avatarName}
      avatarUrl={shell.avatarUrl}
      identity={shell.identity}
      casesBadgeCount={shell.casesBadgeCount}
      hideSidebar
    >
      <ScrollView style={{ flex: 1, backgroundColor: COLORS.OFFW.hex }} contentContainerStyle={{ paddingBottom: 64 }}>
        <View style={{ paddingHorizontal: 22, paddingTop: 28, maxWidth: 760 }}>
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 1.8, textTransform: "uppercase", color: COLORS.AMBER.hex }}>
            🏆 Winnings
          </Text>
          <Text style={{ marginTop: 6, fontFamily: FONTS.display, fontSize: 36, lineHeight: 40, color: COLORS.FOREST.hex }}>
            Your prize history
          </Text>
          <Text
            style={{
              marginTop: 8,
              fontFamily: FONTS.body,
              fontSize: 13.5,
              lineHeight: 21,
              color: STUDENT_THEME.textOnSurfaceDim,
              maxWidth: 580,
            }}
          >
            Every prize you've earned through Coravu, plus the status of each payment.
          </Text>

          {loading ? (
            <View style={{ marginTop: 28, alignItems: "center" }}>
              <ActivityIndicator color={COLORS.AMBER.hex} />
            </View>
          ) : rows.length === 0 ? (
            <View
              style={{
                marginTop: 28,
                paddingVertical: 32,
                paddingHorizontal: 22,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: STUDENT_THEME.border,
                backgroundColor: STUDENT_THEME.surfaceRaisedBg,
                alignItems: "center",
              }}
            >
              <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 18, color: COLORS.FOREST.hex }}>
                No winnings yet
              </Text>
              <Text
                style={{
                  marginTop: 6,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  color: STUDENT_THEME.textOnSurfaceDim,
                  textAlign: "center",
                  maxWidth: 460,
                }}
              >
                Prizes appear here when you win a competition. Keep going.
              </Text>
            </View>
          ) : (
            <View style={{ marginTop: 24, gap: 14 }}>
              {rows.map((r) => (
                <DisbursementCard key={r.id} row={r} />
              ))}
            </View>
          )}
        </View>
      </ScrollView>
    </StudentPortalShell>
  );
}

function DisbursementCard({ row }: { row: StudentDisbursementRow }) {
  const status = row.status;

  const pillTint =
    status === "paid"
      ? COLORS.SAGE.hex
      : status === "forfeited"
        ? STUDENT_THEME.textOnSurfaceFaint
        : status === "ready_to_pay"
          ? COLORS.AMBER.hex
          : STUDENT_THEME.destructive;
  const pillLabel =
    status === "paid"
      ? "Paid"
      : status === "forfeited"
        ? "Forfeited"
        : status === "ready_to_pay"
          ? "Ready to pay"
          : "Pending W-9";

  const taxStatusLine = (() => {
    if (row.tax_info_status === "not_required") {
      return "Prize on the way — no W-9 needed (under $600). We'll be in touch with payment details.";
    }
    if (row.tax_info_status === "received") return "Tax info received. Payment processing.";
    if (row.tax_info_status === "requested") return "Waiting on your tax info — check your email for the link.";
    if (row.tax_info_status === "not_started")
      return "Tax info needed before we can send your prize.";
    return null;
  })();

  return (
    <View
      style={{
        padding: 18,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: STUDENT_THEME.border,
        backgroundColor: STUDENT_THEME.surfaceRaisedBg,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <Text style={{ flex: 1, fontFamily: FONTS.displayMedium, fontSize: 24, color: COLORS.FOREST.hex }}>
          {placeLabel(row.place)} · {row.competition.name}
        </Text>
        <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 28, color: COLORS.AMBER.hex }}>
          {formatUsd(row.amount_cents)}
        </Text>
      </View>

      <View style={{ marginTop: 10, flexDirection: "row", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <View
          style={{
            paddingHorizontal: 9,
            paddingVertical: 3,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: pillTint,
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              color: pillTint,
            }}
          >
            {pillLabel}
          </Text>
        </View>
        {row.payment_rail && row.payment_rail !== "manual" && status === "paid" ? (
          <Text style={{ fontFamily: FONTS.body, fontSize: 11.5, color: STUDENT_THEME.textOnSurfaceDim }}>
            via {row.payment_rail}
            {row.paid_at ? " · " + new Date(row.paid_at).toLocaleDateString() : ""}
          </Text>
        ) : null}
      </View>

      {taxStatusLine && status !== "paid" && status !== "forfeited" ? (
        <Text style={{ marginTop: 12, fontFamily: FONTS.body, fontSize: 13, lineHeight: 19, color: STUDENT_THEME.textOnSurfaceDim }}>
          {taxStatusLine}
        </Text>
      ) : null}

      {status !== "paid" && status !== "forfeited" && row.tax_info_status === "not_started" ? (
        <Pressable
          onPress={() => router.push("/student/tax-info" as Href)}
          style={[
            {
              marginTop: 12,
              alignSelf: "flex-start",
              paddingHorizontal: 14,
              paddingVertical: 9,
              borderRadius: 4,
              backgroundColor: COLORS.AMBER.hex,
            },
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 11,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: COLORS.BLACK.hex,
            }}
          >
            Get tax info link →
          </Text>
        </Pressable>
      ) : null}

      {status === "forfeited" && row.forfeit_reason ? (
        <Text
          style={{
            marginTop: 10,
            paddingHorizontal: 12,
            paddingVertical: 8,
            borderRadius: 4,
            backgroundColor: "rgba(28,43,26,0.04)",
            fontFamily: FONTS.body,
            fontSize: 12,
            color: STUDENT_THEME.textOnSurfaceDim,
            fontStyle: "italic",
          }}
        >
          {row.forfeit_reason}
        </Text>
      ) : null}
    </View>
  );
}
